import React, { useState, useEffect, useCallback } from 'react';
import { findIndex } from 'lodash';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import ViewAutomatedAnalysisLink from 'components/VideoResponse/Grading/ViewAutomatedAnalysisLink';
import VideoResponseModal from './Modal/VideoResponseModal';
import Skipped from './Skipped';
import VideoThumbnail from './VideoThumbnail';
import SharableRecordingLinkContainer from '../SharableRecordingLinkContainer';

const VideoResponse = ({ initialState }) => {
  const { data, meta, i18n } = initialState;
  const { id } = data;
  const { skipped } = meta;

  const [modalVisible, setModalVisible] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState(data.correctAnswer);
  const [feedback, setFeedback] = useState(data.feedback);
  const [criteriaRatings, setCriteriaRatings] = useState(data.criteriaRatings);

  const setCorrect = useCallback(
    (answerId, incomingCorrectAnswer) => {
      if (id === answerId) setCorrectAnswer(incomingCorrectAnswer);
    },
    [id],
  );

  const setVideoFeedback = useCallback(
    (answerId, incomingFeedback) => {
      if (id === answerId) setFeedback(incomingFeedback);
    },
    [id],
  );

  useEffect(() => {
    const setCriteriaScore = (answerId, criteriaId, score) => {
      if (answerId === data.id) {
        const index = findIndex(criteriaRatings, ['id', criteriaId]);
        const criteria = criteriaRatings[index];
        criteria.score = score;
        setCriteriaRatings([
          ...criteriaRatings.slice(0, index),
          criteria,
          ...criteriaRatings.slice(index + 1),
        ]);
      }
    };
    global.pubsub.subscribe('gradingModal:correct', setCorrect);
    global.pubsub.subscribe('gradingModal:feedback', setVideoFeedback);
    global.pubsub.subscribe('grading:rating', setCriteriaScore);
    return () => {
      global.pubsub.unsubscribe('gradingModal:correct', setCorrect);
      global.pubsub.unsubscribe('gradingModal:feedback', setVideoFeedback);
      global.pubsub.unsubscribe('grading:rating', setCriteriaScore);
    };
  }, [criteriaRatings, data.id, setCorrect, setVideoFeedback]);

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const showSharableRecordingLink =
    data.givenAnswer && !skipped && meta.canViewSharableRecordingLink;
  return (
    <div>
      <div className="VideoResponse">
        {skipped && (
          <Skipped
            isCurrentUser={data.isCurrentUser}
            questionType={data.questionType}
            i18n={i18n}
            skipReason={data.skipReason}
          />
        )}
        {!skipped && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
          <div onClick={openModal}>
            <VideoThumbnail
              mediaThumbnailSrc={data.recording?.thumbnail}
              questionType={data.questionType}
            />
          </div>
        )}
      </div>
      {modalVisible && (
        <RenderInBody>
          <ModalContainer
            className="VideoResponseModal ModalTakeover-background Modal-background"
            onEscape={closeModal}
          >
            <VideoResponseModal
              data={data}
              correctAnswer={correctAnswer}
              feedback={feedback}
              setCorrect={setCorrect}
              setFeedback={setVideoFeedback}
              closeModal={closeModal}
              modalVisible={modalVisible}
              criteriaRatings={criteriaRatings}
              ratingOptions={data.ratingOptions}
              i18n={i18n}
              apiKey={meta.apiKey}
              canViewSharableRecordingLink={meta.canViewSharableRecordingLink}
              feedbackCriteriaPresent={meta.feedbackCriteriaPresent}
              displayFeedbackPanel={!data.questionAutomatedAnalysis}
            />
          </ModalContainer>
        </RenderInBody>
      )}
      {showSharableRecordingLink && data.mediaShareLink && (
        <SharableRecordingLinkContainer mediaShareLink={data.mediaShareLink} />
      )}
      {data.questionAutomatedAnalysis && !skipped && (
        <ViewAutomatedAnalysisLink
          onClick={openModal}
          data={data?.metadata?.automated_analysis_status}
        />
      )}
    </div>
  );
};

export default VideoResponse;
